import React from 'react';
import { Box, Typography } from '@mui/material';
import { OPS_COLORS } from '../../constants/operationsDashboardColors';
import { formatStaffingValue } from '../../utils/operationsDashboardUtils';
import { KpiFilterKey } from '../../utils/kpiDashboardFilters';

interface CompactSummaryBarProps {
  scheduled: number;
  present: number;
  presentLate: number;
  absent: number;
  covering: number;
  uncovered: number;
  readinessPercent: number;
  coverageLoad: string;
  activeFilter?: KpiFilterKey;
  onFilterClick?: (filter: KpiFilterKey) => void;
}

const items: Array<{
  key: KpiFilterKey;
  value: (p: CompactSummaryBarProps) => number | string;
  label: string;
  color: string;
}> = [
  { key: 'scheduled', value: (p) => p.scheduled, label: 'Scheduled', color: OPS_COLORS.expected },
  { key: 'present', value: (p) => p.present, label: 'On Time', color: OPS_COLORS.present },
  { key: 'late', value: (p) => p.presentLate, label: 'Late', color: OPS_COLORS.late },
  { key: 'absent', value: (p) => p.absent, label: 'Absent', color: OPS_COLORS.critical },
  { key: 'covering', value: (p) => p.covering, label: 'Covering', color: OPS_COLORS.coverage },
  { key: 'uncovered', value: (p) => p.uncovered, label: 'Needed', color: OPS_COLORS.uncovered },
  { key: 'readiness', value: (p) => `${p.readinessPercent}%`, label: 'Readiness', color: OPS_COLORS.excellent },
  { key: 'load', value: (p) => p.coverageLoad, label: 'Load', color: OPS_COLORS.expected },
];

const CompactSummaryBar: React.FC<CompactSummaryBarProps> = (props) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'nowrap',
      gap: 0,
      height: 36,
      px: 0.75,
      mb: 0.5,
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 1,
      bgcolor: 'background.paper',
      overflowX: 'auto',
      '&::-webkit-scrollbar': { height: 4 },
    }}
  >
    {items.map((item, idx) => {
      const active = props.activeFilter === item.key;
      return (
      <Box
        key={item.key}
        onClick={props.onFilterClick ? () => props.onFilterClick?.(item.key) : undefined}
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 0.4,
          px: 1,
          minWidth: 0,
          flexShrink: 0,
          borderRight: idx < items.length - 1 ? '1px solid' : 'none',
          borderColor: 'divider',
          height: '100%',
          py: 0.5,
          cursor: props.onFilterClick ? 'pointer' : 'default',
          bgcolor: active ? `${item.color}18` : 'transparent',
          '&:hover': props.onFilterClick ? { bgcolor: active ? `${item.color}22` : `${item.color}0c` } : undefined,
        }}
      >
        <Typography fontWeight={700} sx={{ fontSize: '0.8rem', lineHeight: 1, color: item.color }}>
          {typeof item.value(props) === 'number' ? item.value(props) : item.value(props)}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ fontSize: '0.6rem', lineHeight: 1, whiteSpace: 'nowrap' }}
        >
          {item.label}
        </Typography>
      </Box>
      );
    })}
    <Box sx={{ flex: 1, minWidth: 8 }} />
    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.58rem', pr: 0.5, whiteSpace: 'nowrap' }}>
      Working {props.present + props.presentLate} / {props.scheduled}
      {props.uncovered > 0 ? ` · ${formatStaffingValue(props.uncovered)} open` : ''}
    </Typography>
  </Box>
);

export default CompactSummaryBar;
